import { AnyAction } from 'redux';
import { JobType } from './jobs.types';
import { jobsTypes } from './index';
import dotProp from 'dot-prop-immutable';
import { loginTypes } from '../login';
import { offlineTypes } from '../offline';
import { types as meloAttributeTypes } from '../meloAttribute/meloAttribute.actions';

export interface jobsStateType {
    entities: {
        jobs: {
            [key: string]: JobType;
        };
    };
    jobs: string[];
    loading: boolean;
    /**
     * Ob mindestens ein Ladedurchlauf über alle Logins abgeschlossen ist. Unterscheidet
     * "Aufträge noch nicht geladen" von "Auftrag existiert nicht mehr" (#336).
     */
    loaded: boolean;
    sending: boolean;
    blocked: boolean;
    sortSettings?: {
        fields: {fieldName: string, direction: 'ASC' | 'DESC'}[];
    }
}

const initialState: jobsStateType = {
    entities: {
        jobs: {},
    },
    jobs: [],
    loading: false,
    loaded: false,
    sending: false,
    blocked: false,
    sortSettings: undefined,
};

const reducer = (state = initialState, action: AnyAction) => {
    switch (action.type) {
        case jobsTypes.LOAD_JOBS_STATUS: {
            return {
                ...state,
                blocked: action.loading,
                /*
                 * Ein abgeschlossener Durchlauf (loading -> false) heißt: Die Saga hat alle Logins
                 * abgearbeitet. Erst danach darf ein fehlender Auftrag als "existiert nicht"
                 * gelten -- vorher wäre ein Deep-Link vor dem ersten Laden nicht davon zu
                 * unterscheiden (#336).
                 */
                loaded: state.loaded || !action.loading,
            };
        }
        case jobsTypes.JOBS_ADD_ENTITIES: {
            let {
                data: { entities, result },
            } = action;

            let uniqueJobIds = Array.from(new Set<string>([...state.jobs, ...result]));
            return {
                ...state,
                loading: false,
                entities: {
                    ...state.entities,
                    jobs: {
                        ...state.entities.jobs,
                        ...entities.jobs,
                    },
                },
                jobs: uniqueJobIds,
            };
        }

        case jobsTypes.JOBS_REMOVE_BY_IDS: {
            const { jobIds } = action;
            const removeSet = new Set<string>(jobIds);

            return {
                ...state,
                jobs: state.jobs.filter((id) => !removeSet.has(id)),
                entities: {
                    ...state.entities,
                    jobs: Object.entries(state.entities.jobs).reduce((res: any, [k, j]) => {
                        if (!removeSet.has(k)) {
                            res[k] = j;
                        }
                        return res;
                    }, {}),
                },
            };
        }
        case jobsTypes.LOAD_JOBS: {
            return {
                ...state,
                loading: true,
            };
        }
        case jobsTypes.LOAD_JOBS_DONE: {
            const { jobs } = action;
            return {
                ...state,
                jobs: jobs,
                loading: false,
            };
        }
        case jobsTypes.SEND_METER_TASK_RESULT:
        case jobsTypes.SEND_METER_TASK_RESULTS: {
            return {
                ...state,
                sending: true,
            };
        }
        case jobsTypes.SEND_METER_TASK_RESULT_ERROR: {
            return {
                ...state,
                sending: false,
            };
        }
        case offlineTypes.QUEUE_ACTION: {
            if (action.action?.type !== jobsTypes.SEND_METER_TASK_RESULTS) {
                return state;
            }

            return {
                ...state,
                sending: false,
            };
        }
        case jobsTypes.SEND_METER_TASK_RESULT_DONE: {
            let { job } = action;
            job = job as JobType;

            let newState = dotProp.set(state, `sending`, false);
            return dotProp.set(newState, `entities.jobs.${job.id}`, job);
        }
        case loginTypes.LOGOUT: {
            return initialState;
        }
        case jobsTypes.SET_SORTSETTINGS: {
            const {settings} = action;
            return dotProp.set(state, `sortSettings`, settings);
        }
        case meloAttributeTypes.MELO_ATTRIBUTE_UPSERTED: {
            const { meLoId, item } = action;
            return {
                ...state,
                entities: {
                    ...state.entities,
                    jobs: Object.entries(state.entities.jobs).reduce((res: any, [id, job]) => {
                        const typedJob = job as JobType;
                        if (typedJob.meter?.meLoId === meLoId) {
                            const existing = typedJob.meloAttributes ?? [];
                            const replaced = existing.some((a) => a.id === item.id);
                            const meloAttributes = replaced
                                ? existing.map((a) => (a.id === item.id ? item : a))
                                : [...existing, item];
                            res[id] = { ...typedJob, meloAttributes };
                        } else {
                            res[id] = job;
                        }
                        return res;
                    }, {}),
                },
            };
        }
        case meloAttributeTypes.MELO_ATTRIBUTE_REMOVED: {
            const { meLoId, attributeId } = action;
            return {
                ...state,
                entities: {
                    ...state.entities,
                    jobs: Object.entries(state.entities.jobs).reduce((res: any, [id, job]) => {
                        const typedJob = job as JobType;
                        if (typedJob.meter?.meLoId === meLoId) {
                            const meloAttributes = (typedJob.meloAttributes ?? []).filter((a) => a.id !== attributeId);
                            res[id] = { ...typedJob, meloAttributes };
                        } else {
                            res[id] = job;
                        }
                        return res;
                    }, {}),
                },
            };
        }
        default:
            return state;
    }
};

export default reducer;
